import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getLenis } from '../hooks/useSmoothScroll';
import { SUPPORT_EMAIL } from '../config';
import styles from './Nav.module.css';

const NAV_LINKS = [
  { key: 'about', to: '/about', label: 'About' },
  { key: 'nights', to: '/nights', label: 'Revive Nights' },
  { key: 'believe', to: '/believe', label: 'What We Believe' },
  { key: 'faq', to: '/faq', label: 'FAQ' },
  // Static page outside the router — must be a hard link, not a NavLink.
  { key: 'yearinreview', href: '/yearinreview', label: 'Year in Review' },
];

export default function Nav({ onGive, menuOpen, setMenuOpen, links }) {
  // Admin-managed visibility; default everything on.
  const navLinks = NAV_LINKS.filter((l) => !links || links[l.key] !== false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Stop Lenis while menu open
  useEffect(() => {
    const l = getLenis();
    if (!l) return;
    if (menuOpen) l.stop();
    else l.start();
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [pathname, setMenuOpen]);

  // Home desktop hero is a full-bleed photo; mobile is cream. Make the
  // nav transparent (with cream type) only on desktop home before scroll.
  const [isDesktop, setIsDesktop] = useState(
    typeof window === 'undefined' ? true : window.innerWidth >= 761
  );
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 761);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const transparent = isHome && !scrolled && isDesktop;

  return (
    <>
      <header
        className={`${styles.bar} ${scrolled ? styles.barScrolled : ''} ${transparent ? styles.barOnImage : ''}`}
      >
        <Link to="/" className={styles.brand} aria-label="Revive Worship — home">
          <img
            src={transparent ? '/images/logo-white.png' : '/images/logo-black.png'}
            alt="Revive Worship"
            className={styles.brandLogo}
          />
        </Link>

        <nav className={styles.linksWrap} aria-label="Primary">
          <ul className={styles.links}>
            {navLinks.map((l) => (
              <li key={l.to || l.href}>
                {l.href ? (
                  <a href={l.href} className={styles.link}>
                    {l.label}
                  </a>
                ) : (
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `${styles.link} ${isActive ? styles.linkActive : ''}`
                    }
                  >
                    {l.label}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
          <button className={styles.giveBtn} onClick={onGive}>
            Give
          </button>
        </nav>

        <button
          className={styles.menuToggle}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span className={`${styles.menuLine} ${menuOpen ? styles.menuLineOpen1 : ''}`} />
          <span className={`${styles.menuLine} ${menuOpen ? styles.menuLineOpen2 : ''}`} />
        </button>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className={styles.sheet}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className={styles.sheetPanel}
              initial={{ y: '-100%' }}
              animate={{ y: 0 }}
              exit={{ y: '-100%' }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <ul className={styles.sheetList}>
                {navLinks.map((l, i) => (
                  <motion.li
                    key={l.to || l.href}
                    initial={{ opacity: 0, y: 26 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.12 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {l.href ? (
                      <a href={l.href} className={styles.sheetLink}>
                        <span className={styles.sheetIdx}>0{i + 1}</span>
                        <span className={styles.sheetText}>{l.label}</span>
                        <span className={styles.sheetArrow}>↗</span>
                      </a>
                    ) : (
                      <NavLink to={l.to} className={styles.sheetLink}>
                        <span className={styles.sheetIdx}>0{i + 1}</span>
                        <span className={styles.sheetText}>{l.label}</span>
                        <span className={styles.sheetArrow}>↗</span>
                      </NavLink>
                    )}
                  </motion.li>
                ))}
              </ul>

              <div className={styles.sheetFoot}>
                <button
                  className={styles.sheetGive}
                  onClick={() => { setMenuOpen(false); setTimeout(onGive, 80); }}
                >
                  Give to Revive
                  <span aria-hidden="true">→</span>
                </button>
                <a className={styles.sheetEmail} href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
